import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { DiscountFormSchemaValues } from 'src/features/discounts/utils/formSchema';
import { useListDiscountPolicies } from 'src/features/discountPolicies/api/useListDiscountPolicies';
import { discountsMessages } from 'src/features/discounts/utils/messages';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useListSubscriptions } from 'src/features/subscriptions/api/useListSubscriptions';
import { useGetDiscountPolicy } from 'src/features/discountPolicies/api/useGetDiscountPolicy';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { useListSubscriptionItems } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { useParams } from 'react-router-dom';
import React from 'react';
import { useGetSubscription } from 'src/features/subscriptions/api/useGetSubscription';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';

type DiscountFormProps = {
    form: UseFormReturn<DiscountFormSchemaValues>;
    onSubmit: (values: DiscountFormSchemaValues) => void;
    isEditMode?: boolean;
};

function DiscountForm({ onSubmit, form, isEditMode = false }: DiscountFormProps) {
    const { formatMessage } = useIntl();
    const { subscriptionId } = useParams();

    const [searchDiscountPolicy, setSearchDiscountPolicy] = React.useState<string>('');

    const disableSubscriptionSelect = Boolean(subscriptionId);

    const { discount_policy, subscription, start_date } = form.watch();

    const nextDayFromStartDate = new Date(start_date);
    nextDayFromStartDate.setDate(nextDayFromStartDate.getDate() + 1);

    const { data: dashboardSubscription } = useGetSubscription({
        id: subscriptionId || '',
        config: { enabled: !!subscriptionId },
    });

    const { data: discountPolicies } = useListDiscountPolicies({
        search: searchDiscountPolicy,
        ...(dashboardSubscription && { service_plans: [dashboardSubscription.service_plan.id] }),
    });

    const { data: subscriptionItems } = useListSubscriptionItems({
        subscriptionId: subscription,
        config: { enabled: !!subscription },
    });

    const { data: discountPolicy } = useGetDiscountPolicy({
        policyId: discount_policy,
        config: { enabled: !!discount_policy },
    });

    const { data: subscriptions } = useListSubscriptions({
        currency: discountPolicy?.currency,
        config: { enabled: !!discountPolicy },
    });

    const filteredSubscriptionItems = React.useMemo(() => {
        const discountServicePlanItems = discountPolicy?.service_plan_items?.map((p) => p.id) ?? [];

        return subscriptionItems?.results?.filter((item) =>
            discountServicePlanItems.includes(item.service_plan_item?.id),
        );
    }, [discountPolicy, subscriptionItems]);

    const filteredSubscriptions = React.useMemo(() => {
        const discountServicePlans = discountPolicy?.service_plans?.map((p) => p.id) ?? [];

        return subscriptions?.results?.filter((item) => discountServicePlans.includes(item.service_plan?.id));
    }, [discountPolicy, subscriptions]);

    const subscriptionsToUse =
        disableSubscriptionSelect && dashboardSubscription ? [dashboardSubscription] : filteredSubscriptions;

    const discountHasManualValidDuration = discountPolicy?.manual_valid_duration ?? false;

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SearchSelectFormField
                name="discount_policy"
                control={form.control}
                label={formatMessage(discountPoliciesMessages.discountPolicy)}
                items={discountPolicies?.results ?? []}
                clearable
                placeholder={`${formatMessage(discountPoliciesMessages.discountPolicy)}...`}
                searchPlaceholder={formatMessage(discountPoliciesMessages.searchDiscountPolicy)}
                emptyTitle={formatMessage(discountPoliciesMessages.noDiscountPolicy)}
                emptyDescription={formatMessage(discountPoliciesMessages.noDiscountPolicyFound)}
                getItemValue={(policy) => policy.id}
                getItemLabel={(policy) => policy.name}
                getItemKeywords={(policy) => [policy.name]}
                loadOptions={setSearchDiscountPolicy}
                disabled={isEditMode}
            />
            <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(discountsMessages.startDate)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    captionLayout="dropdown"
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: new Date() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {discountHasManualValidDuration && (
                <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(discountsMessages.endDate)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <CalendarWithTime
                                        captionLayout="dropdown"
                                        dateISO={field.value}
                                        onChange={field.onChange}
                                        disabledDateRange={{ before: nextDayFromStartDate }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            <FormField
                control={form.control}
                name="subscription"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...subscriptionsMessages.subscription} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={disableSubscriptionSelect || discountPolicy === undefined}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(subscriptionsMessages.subscription)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {subscriptionsToUse?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {`${option.reference}-${option.service_plan.name}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="subscription_item"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...subscriptionItemsMessages.item} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={subscription === undefined}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(subscriptionItemsMessages.item)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {filteredSubscriptionItems?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.service_plan_item.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default DiscountForm;
